import { Box, VStack } from '@web/components/atoms/Layout'
import { Text } from '@web/components/page-components/Text'
import {
  GivingFrequencySelector,
  PayByInstallments,
  PaymentMarks,
  TermsAndConditions,
  useDonationFormContext,
} from '@web/components/page-components/DonationForm'
import { ComponentType, DonationFrequency } from '@web/graphql-components'
import { DonationFormStepProps } from './utils'
import DollarHandles from './DollarHandles'
import PaymentButtons from './PaymentButtons'
import { useEffect, useState } from 'react'
import { useMergeFields } from '@web/components/page-components/Text/MergeTags/MergeTag'
import { usePageStore } from 'apps/fe-donation/libs/hooks/usePageStore'
import { PageButton } from '@web/components/page-components/PageButton'
import DonationStepTabs from '../../../../../packages/components/src/lib/page-components/DonationForm/components/atoms/DonationStepTab'
import DonationFormEmailInput from '../../../../../packages/components/src/lib/page-components/DonationForm/components/atoms/DonationFormEmailInput'
import { DONOR_MERGE_TAGS, GIVING_MERGE_TAGS } from '@web/components/page-components/Text/MergeTags/utils'
import MatchedGiving from './MatchedGiving'
import CallUsPopupForm from './CallUsPopupForm'
import { usePaymentStore } from 'apps/fe-donation/libs/hooks/usePaymentStore'

type DonationFormStep1Props = DonationFormStepProps & {
  withPayment?: boolean
}

const [FIRST_NAME_TAG, LAST_NAME_TAG, EMAIL_TAG, , MOBILE_TAG] = DONOR_MERGE_TAGS
const [, AMOUNT_TAG] = GIVING_MERGE_TAGS

const DonationFormStep1 = (donationProps: DonationFormStep1Props) => {
  const formValues = useDonationFormContext((state) => state.formValues)
  const actions = useDonationFormContext((state) => state.actions)
  const { setFormValues, nextStep, validateForm } = actions
  const mergeFields = useMergeFields((state) => state.mergeFields)
  const [isValid, setIsValid] = useState(false)

  useEffect(() => {
    if (
      !mergeFields ||
      formValues.extraFields?.firstName ||
      formValues.extraFields?.lastName ||
      formValues.extraFields?.email
    ) {
      return
    }

    const amount =
      Number(
        mergeFields.get(AMOUNT_TAG.MergeFieldSettingId) ||
          mergeFields.get(AMOUNT_TAG.MergeFieldSettingId.toLowerCase()) ||
          0,
      ) || formValues.amount

    const mergeFieldValues = {
      amount,
      extraFields: {
        email: mergeFields.get(EMAIL_TAG.MergeFieldSettingId) ?? '',
        firstName: mergeFields.get(FIRST_NAME_TAG.MergeFieldSettingId) ?? '',
        lastName: mergeFields.get(LAST_NAME_TAG.MergeFieldSettingId) ?? '',
        mobile: mergeFields.get(MOBILE_TAG.MergeFieldSettingId) ?? '',
      },
    }

    setFormValues({
      ...formValues,
      ...mergeFieldValues,
    })
    usePageStore.setState({ amount: mergeFieldValues.amount ?? 0 })
  }, [mergeFields.size])

  const {
    title,
    donationFrequency,
    donationFrequencyDefault,
    dollarHandlesDefault,
    matchedGivings,
    legalUrl,
    styles,
    withPayment = true,
  } = donationProps

  const dollarHandles =
    styles?.options?.dollarHandleOverrides?.[formValues.givingFrequency ?? ''] || donationProps.dollarHandles
  const hasPaypal = usePaymentStore(
    (state) => !!state.paymentSettings?.donationPagePaymentProviderSettings?.payPalMerchantSetting?.merchantId,
  )

  useEffect(() => {
    if (withPayment) {
      return
    }

    setIsValid(validateForm(false, { email: true }))
  }, [formValues])

  return (
    <>
      {title && <Text id="donation-form-header" type={ComponentType.Text} textValue={title} />}
      {!withPayment && <DonationStepTabs />}
      {(donationFrequency.length > 1 ||
        (donationFrequency.length === 1 && donationFrequency[0] !== DonationFrequency.OneTime)) &&
        !formValues.isPayByInstallments && (
          <GivingFrequencySelector frequencies={donationFrequency} defaultFrequency={donationFrequencyDefault} />
        )}
      <DollarHandles withPayment={withPayment} handles={dollarHandles} defaultHandle={dollarHandlesDefault} />
      {styles?.options?.hasCallUsForm && (
        <Box w="100%" mt="-16px">
          <CallUsPopupForm />
        </Box>
      )}
      {styles?.options?.payByInstallmentFrequencies?.length > 0 && <PayByInstallments />}
      {/* assuming we can only be showing one matched giving at a time */}
      {matchedGivings?.map((matchedGiving) => (
        <MatchedGiving key={matchedGiving.id} />
      ))}
      {withPayment && (
        <>
          <DonationFormEmailInput hideLabel={true} />
          <VStack gap="16px" w="100%">
            <PaymentButtons />
            <TermsAndConditions legalUrl={legalUrl} />
          </VStack>
        </>
      )}
      {!withPayment && (
        <VStack gap="16px" w="100%">
          <PageButton
            id="next-step-from-1"
            name="Primary"
            buttonText="Next"
            disabled={!isValid}
            onClick={() => {
              nextStep()
            }}
            width="100%"
          />
        </VStack>
      )}
      <PaymentMarks
        hasPayPal={
          hasPaypal && (!formValues.givingFrequency || formValues.givingFrequency === DonationFrequency.OneTime)
        }
      />
    </>
  )
}

export { DonationFormStep1 }
